/**
 * Local `amazon-connect-chatjs` session loader for the dashboard chat drawer.
 *
 * `@authe/fe-commons/connect` exposes a framework-agnostic `createChatSession`,
 * but it loads chatjs via `import(/* @vite-ignore * / moduleSpecifier)` with a
 * VARIABLE specifier (so the package stays importable in Node/tests without the
 * optional peer dep). Vite can't statically analyse that, leaves it as a bare
 * runtime specifier, and never bundles chatjs — so the drawer's chat silently
 * fails to connect in the built app.
 *
 * This mirror uses a LITERAL dynamic import so Vite code-splits chatjs into its
 * own lazily-loaded chunk that actually resolves at runtime. The contact-form
 * POST (`startChat`), schema and types still come from `@authe/fe-commons/connect`;
 * only the chatjs handshake→session step is overridden here. (The `global` →
 * `globalThis` shim chatjs's browserified AWS SDK needs is already set in
 * `vite.config.ts`.)
 */
import type {
  ChatJsSession,
  CreateChatSessionOptions,
  StartChatHandshake,
} from "@authe/fe-commons/connect";

/** chatjs `connect` namespace — the small slice we use. */
interface ChatJsNamespace {
  ChatSession: {
    setGlobalConfig: (config: unknown) => void;
    create: (args: unknown) => ChatJsSession;
    SessionTypes: { CUSTOMER: string; AGENT: string };
  };
}

/**
 * Create (but do not connect) a chatjs CUSTOMER session from a handshake.
 * Call `await session.connect()` once the message handlers are wired.
 */
export async function createChatSession(
  handshake: StartChatHandshake,
  opts: CreateChatSessionOptions = {},
): Promise<ChatJsSession> {
  const mod = (await import("amazon-connect-chatjs")) as unknown as
    | ChatJsNamespace
    | { default: ChatJsNamespace };
  const connect = "ChatSession" in mod ? mod : mod.default;
  const region = opts.region ?? handshake.region;

  connect.ChatSession.setGlobalConfig({
    region,
    loggerConfig: { level: opts.logLevel ?? "ERROR" },
  });

  return connect.ChatSession.create({
    chatDetails: {
      contactId: handshake.contact_id,
      participantId: handshake.participant_id,
      participantToken: handshake.participant_token,
    },
    type: connect.ChatSession.SessionTypes.CUSTOMER,
    options: { region },
  });
}
